import { promises as fs } from 'node:fs';
import { consola } from 'consola';
import path from 'node:path';
import type { Document } from 'yaml';
import { parseConfig, readConfig, stringifyConfig } from '../config/io.js';
import { httpServices, type HttpService } from '../config/http-services.js';
import {
  containerConfigPath,
  containerConfigsDir,
  containerDir,
  containerEnvPath,
  monocerosHome as defaultMonocerosHome,
} from '../config/paths.js';
import {
  ensureEnvGitignored,
  ensureEnvVars,
  hasVarPlaceholder,
  GIT_IDENTITY_VAR,
} from '../config/env-file.js';
import {
  featureOptionHints,
  featureOptionVarName,
  FEATURE_HEADER_WIDTH,
} from '../init/feature-doc.js';
import { loadFeatureManifestSummary } from '../init/manifest.js';
import {
  buildLanguageHeaderLines,
  buildServiceHeaderLines,
  substituteName,
} from '../init/catalog-header.js';
import {
  buildMcpConnectorDoc,
  findMcpConnector,
  mcpConnectorNames,
  mcpHeaderCommentBefore,
} from '../init/mcp-doc.js';
import {
  loadDescriptorCatalog,
  type CatalogComponent,
} from '../catalog/load.js';
import type { Descriptor } from '../catalog/descriptor.js';
import {
  collectGitCredentials,
  resolveProvider,
} from '../devcontainer/credentials.js';
import {
  resolveContainerRepoTokens,
  formatUnauthenticatedRepos,
  type MissingRepoToken,
} from '../apply/repo-token.js';
import { REPO_DOCS_URL } from '../config/schema.js';
import {
  findRunningContainerByLocalFolder,
  realContainerExec,
  type ContainerExec,
  type DockerLookupExec,
} from '../devcontainer/locate-running.js';
import { proxyHostPort, readMonocerosConfig } from '../config/global.js';
import {
  KNOWN_PROVIDER_HOSTS,
  PROVIDER_FEATURE_SELECTOR,
  PROVIDER_VALUES,
  REGEX,
  isValidEmail,
  portNumber,
  type RepoProvider,
} from '../config/schema.js';
import { loadComponentCatalog } from '../init/components.js';
import {
  attachToProxyNetwork,
  ensureProxy,
  maybeStopProxy,
  type DockerExec as ProxyDockerExec,
} from '../proxy/index.js';
import {
  proxyUrlsFor,
  removeDynamicConfig,
  serviceProxyUrl,
  writeDynamicConfig,
} from '../proxy/dynamic.js';
import { preflightHostPort } from '../proxy/port-check.js';
import {
  BUILTIN_LANGUAGES,
  LANGUAGE_CATALOG,
  curatedServiceEnvDefaults,
  deriveServiceName,
  expandCuratedService,
  curatedServiceExampleVolumes,
  isCuratedService,
  knownLanguages,
  parseLanguageSpec,
} from '../create/catalog.js';
import {
  renderServiceObjectBody,
  renderCustomService,
  exampleVolumesComment,
  customServiceHint,
} from '../init/service-doc.js';
import {
  cloneUrl,
  deriveRepoName,
  normalizeOptions,
} from '../create/scaffold.js';
import { solutionConfigToCreateOptions } from '../config/transform.js';
import { writeBriefing } from '../briefing/index.js';
import type { FeatureOptions, RepoEntry } from '../create/types.js';
import {
  addAptPackagesToDoc,
  addFeatureToDoc,
  addInstallUrlToDoc,
  addLanguageToDoc,
  addMcpToDoc,
  addPortsToDoc,
  addRepoToDoc,
  addServiceEntryToDoc,
  ensureContainerGitUserPlaceholder,
  relocateLeakedSectionComments,
  removeAptPackagesFromDoc,
  removeFeatureFromDoc,
  removeInstallUrlFromDoc,
  removeLanguageFromDoc,
  removeMcpFromDoc,
  removePortsFromDoc,
  removeRepoFromDoc,
  removeServiceFromDoc,
  setDefaultPortInDoc,
} from './yml.js';

/**
 * `monoceros add-*` / `monoceros remove-*` — edit the yml at
 * `<MONOCEROS_HOME>/container-configs/<name>.yml` for one container.
 *
 * No cwd magic. The first positional argument is always the container
 * name; the command looks up the yml via convention. Comment-preserving
 * AST mutation; the builder then runs `monoceros apply <name>` to
 * materialize.
 */

export interface ModifyLogger {
  info: (message: string) => void;
  success: (message: string) => void;
  warn: (message: string) => void;
}

export interface ModifyOptions {
  /** Container name — resolves to `<home>/container-configs/<name>.yml`. */
  name: string;
  logger?: ModifyLogger;
  output?: (line: string) => void;
  /** Override the resolved MONOCEROS_HOME. Tests inject a tmpdir. */
  monocerosHome?: string;
}

export interface AddLanguageInput extends ModifyOptions {
  language: string;
}
export interface AddServiceInput extends ModifyOptions {
  service: string;
  /**
   * Override the compose service name. Required to add the same image
   * more than once (two postgres servers → `--as postgres-app` /
   * `--as postgres-analytics`) and to disambiguate two custom images
   * that derive the same name.
   */
  as?: string;
}
export interface AddAptPackagesInput extends ModifyOptions {
  packages: string[];
}
export interface AddFeatureInput extends ModifyOptions {
  ref: string;
  options?: FeatureOptions;
}
export interface AddFromUrlInput extends ModifyOptions {
  url: string;
}
export interface AddMcpServerInput extends ModifyOptions {
  /** Catalog connector selector, e.g. `context7`. */
  connector: string;
  options?: FeatureOptions;
}
export interface AddRepoInput extends ModifyOptions {
  url: string;
  /**
   * Explicit destination path under `projects/`. Subfolders allowed
   * via `/` (e.g. `apps/web`). When omitted, the URL-derived single-
   * segment default is used (`https://.../foo.git` → `foo` →
   * `projects/foo/`).
   */
  path?: string;
  /**
   * Optional per-repo git committer identity override. Both name and
   * email must be set together; one alone is a usage error. Falls
   * back to the container-level `git.user` (which itself falls back
   * to the host's `git config --global`) when omitted.
   */
  gitName?: string;
  gitEmail?: string;
  /**
   * Git provider hint. Required when the URL host is not one of the
   * three canonical ones (github.com / gitlab.com / bitbucket.org);
   * optional otherwise. Validated against `PROVIDER_VALUES`.
   */
  provider?: string;
  /**
   * Test injection points for the on-the-fly-clone path (the part
   * that runs after the yml mutation when the container is up).
   */
  containerLookupDocker?: DockerLookupExec;
  containerExec?: ContainerExec;
}

export interface RemoveLanguageInput extends ModifyOptions {
  language: string;
}
export interface RemoveServiceInput extends ModifyOptions {
  service: string;
}
export interface RemoveAptPackagesInput extends ModifyOptions {
  packages: string[];
}
export interface RemoveFeatureInput extends ModifyOptions {
  ref: string;
}
export interface RemoveFromUrlInput extends ModifyOptions {
  url: string;
}
export interface RemoveMcpServerInput extends ModifyOptions {
  connector: string;
}
export interface RemoveRepoInput extends ModifyOptions {
  /** url or (effective) name — `monoceros remove-repo` accepts either. */
  target: string;
}

export interface AddPortInput extends ModifyOptions {
  ports: number[];
  /**
   * When true, the (single) port in `ports` is moved to / inserted at
   * the front of `routing.ports` — making it the bare
   * `<name>.localhost` default route. Only valid with exactly one
   * port in the args; multiple ports + `asDefault` is a usage error.
   */
  asDefault?: boolean;
  /** Override the docker exec used by the Traefik proxy lifecycle. */
  proxyDocker?: ProxyDockerExec;
  /**
   * Override the docker exec used to locate the running container for
   * the proxy-network attach (#74). Tests inject a fake.
   */
  containerLookupDocker?: DockerLookupExec;
}
export interface RemovePortInput extends ModifyOptions {
  ports: number[];
  /** Override the docker exec used by the Traefik proxy lifecycle. */
  proxyDocker?: ProxyDockerExec;
  /** Unused here; `syncPortsToProxy` is shared with add-port (#74). */
  containerLookupDocker?: DockerLookupExec;
}

export type ModifyResult =
  | { status: 'no-change' }
  | { status: 'updated'; changedPaths: string[] };

type YmlMutator = (doc: Document) => boolean;

// ─── add-* ────────────────────────────────────────────────────────

export function runAddLanguage(input: AddLanguageInput): Promise<ModifyResult> {
  const spec = parseLanguageSpec(input.language);
  if (
    !spec ||
    (!BUILTIN_LANGUAGES.has(spec.name) && !LANGUAGE_CATALOG[spec.name])
  ) {
    throw new Error(
      `Unknown language: ${input.language}. Known: ${knownLanguages().join(', ')}.`,
    );
  }
  // Mirror init: surface the version inline (explicit `:version` wins over the
  // catalog default) and the language's `surface: yml` options as the object
  // form, so the builder sees what's editable.
  const entry = LANGUAGE_CATALOG[spec.name];
  const version = spec.version ?? entry?.defaultVersion;
  const options = entry?.ymlOptions;
  return mutate(input, (doc) =>
    addLanguageToDoc(doc, spec.name, {
      ...(version !== undefined ? { version } : {}),
      ...(options && Object.keys(options).length > 0 ? { options } : {}),
      // Same header `init` writes: one page per language, whatever version
      // this entry pins.
      headerLines: buildLanguageHeaderLines(
        spec.name,
        FEATURE_HEADER_WIDTH,
      ).map((l) => substituteName(l, input.name)),
    }),
  );
}

export async function runAddService(
  input: AddServiceInput,
): Promise<ModifyResult> {
  // Curated catalog name → expand to a full active object block.
  // Anything else → treat the argument as an image, derive the service
  // name from it, and drop in a commented scaffold for the fields
  // Monoceros can't know.
  const arg = input.service;
  const curated = isCuratedService(arg);

  // `--as` overrides the compose service name (default: the curated
  // name, or one derived from the image). Validate the override here so
  // the builder gets a focused message rather than a schema round-trip
  // error. Mirrors the schema's SERVICE_NAME_RE.
  if (input.as !== undefined && !/^[a-z0-9][a-z0-9_-]*$/.test(input.as)) {
    throw new Error(
      `Invalid --as name ${JSON.stringify(input.as)}. Use lowercase letters, digits, '_' or '-' (must start with a letter or digit).`,
    );
  }
  const name = input.as ?? (curated ? arg : deriveServiceName(arg));
  const image = curated ? expandCuratedService(arg).image : arg;
  // Render the block under the resolved name. For curated services the
  // expansion carries the catalog name, so override it before rendering.
  const custom = curated ? null : renderCustomService(name, arg);
  // The scaffold's `values resolved from <name>.env` names this container.
  if (custom) custom.comment = substituteName(custom.comment, input.name);
  const bodyLines = curated
    ? renderServiceObjectBody({ ...expandCuratedService(arg), name })
    : custom!.bodyLines;
  // Curated services with example bind-mounts (e.g. Keycloak) ride a
  // commented `volumes:` scaffold the builder uncomments + edits; custom
  // images carry their own full field scaffold.
  const scaffoldComment = curated
    ? exampleVolumesComment(curatedServiceExampleVolumes(arg))
    : custom!.comment;

  const result = await mutate(input, (doc) => {
    const r = addServiceEntryToDoc(
      doc,
      name,
      image,
      bodyLines,
      scaffoldComment,
      // Built from the catalog name, not `--as`: two postgres services under
      // different names are documented by the one postgres page, and the
      // product name in the header is what explains that to whoever renamed it.
      curated
        ? buildServiceHeaderLines(arg, FEATURE_HEADER_WIDTH).map((l) =>
            substituteName(l, input.name),
          )
        : undefined,
    );
    if (r.outcome === 'conflict') {
      throw new Error(
        `A service named '${name}' already exists with a different image (${r.existingImage}). ` +
          `Add it under a different name with \`--as <name>\`, or remove the existing one first ` +
          `(\`monoceros remove-service ${input.name} ${name}\`).`,
      );
    }
    return r.outcome === 'added';
  });

  // Curated service → seed its env dev-defaults into <name>.env (the
  // same ${KEY} placeholders the expanded block carries), mirroring
  // init and add-feature. Keys are image-dictated (POSTGRES_USER, …),
  // so --as renaming the service doesn't change them. Custom images
  // get the fill-in-the-scaffold hint instead — Monoceros can't know
  // their vars.
  if (result.status === 'updated') {
    if (curated) {
      const defaults = curatedServiceEnvDefaults(arg);
      if (Object.keys(defaults).length > 0) {
        const home = input.monocerosHome ?? defaultMonocerosHome();
        await ensureEnvGitignored(containerConfigsDir(home));
        const seeded = await ensureEnvVars(
          containerEnvPath(input.name, home),
          input.name,
          defaults,
        );
        if (seeded.added.length > 0) {
          (input.logger ?? defaultLogger()).info(
            `Seeded ${seeded.added.join(', ')} into ${input.name}.env (dev-defaults — change them there if needed).`,
          );
        }
      }
    } else {
      (input.logger ?? defaultLogger()).info(customServiceHint(name));
    }
  }
  return result;
}

export function runAddAptPackages(
  input: AddAptPackagesInput,
): Promise<ModifyResult> {
  if (input.packages.length === 0) {
    throw new Error(
      'No package names given. Usage: monoceros add-apt-packages <containername> -- <pkg> [<pkg> …].',
    );
  }
  return mutate(input, (doc) => addAptPackagesToDoc(doc, input.packages));
}

/**
 * Regenerate the AI-tool briefing (AGENTS.md / CLAUDE.md) from the current yml.
 * Only the modify commands that take effect LIVE call this - add/remove-port
 * hot-reload the Traefik routes, add-repo clones on the fly - so their change
 * is in effect immediately and the briefing's "Exposed ports" / "Cloned repos"
 * sections must not lag until the next apply. The apply-only commands
 * (features, languages, …) deliberately do NOT: their change is not
 * materialized yet, so refreshing the briefing would over-claim.
 *
 * Best-effort: skipped when the container was never materialized, and any
 * failure is swallowed (the yml write stands; the next apply regenerates the
 * briefing regardless).
 */
async function refreshBriefingFromYml(
  name: string,
  home: string,
): Promise<void> {
  const targetDir = containerDir(name, home);
  try {
    await fs.access(targetDir);
  } catch {
    return; // never applied — first apply writes the briefing
  }
  try {
    const parsed = await readConfig(containerConfigPath(name, home));
    const globalConfig = await readMonocerosConfig({ monocerosHome: home });
    const createOpts = normalizeOptions(
      solutionConfigToCreateOptions(
        parsed.config,
        globalConfig?.defaults?.features ?? {},
      ),
    );
    const components = await loadComponentCatalog();
    await writeBriefing({ targetDir, createOpts, components });
  } catch {
    /* best-effort: the next apply regenerates the briefing */
  }
}

export async function runAddRepo(input: AddRepoInput): Promise<ModifyResult> {
  const url = input.url.trim();
  if (url.length === 0) {
    throw new Error(
      'Missing repo URL. Usage: monoceros add-repo <containername> <url>.',
    );
  }
  const path = (input.path ?? deriveRepoName(url)).trim();
  // --git-name and --git-email come as a pair. Reject half-set input
  // loudly instead of silently dropping it.
  const hasName =
    typeof input.gitName === 'string' && input.gitName.trim().length > 0;
  const hasEmail =
    typeof input.gitEmail === 'string' && input.gitEmail.trim().length > 0;
  if (hasName !== hasEmail) {
    throw new Error(
      '--git-name and --git-email must be set together. Pass both, or neither.',
    );
  }
  // Validate the email eagerly at the flag entry — the schema defers
  // email format to apply (to allow `${VAR}` placeholders from the
  // hand-edited yml), so a typo'd literal would otherwise only surface
  // at apply. A `${VAR}` placeholder is allowed through here too, in
  // case the builder wants to manage the value in <name>.env.
  if (hasEmail) {
    const email = input.gitEmail!.trim();
    if (!isValidEmail(email) && !hasVarPlaceholder(email)) {
      throw new Error(
        `Invalid --git-email '${email}': must be a valid email or a \${VAR} placeholder resolved from <name>.env.`,
      );
    }
  }
  // --provider validation:
  //   - host is canonical (github.com / gitlab.com / bitbucket.org):
  //       * no --provider → fine, auto-detected at apply time
  //       * --provider matches canonical → accepted, written to yml
  //         (harmless; round-trip stays clean)
  //       * --provider contradicts canonical → reject loudly
  //   - host is non-canonical:
  //       * --provider given (valid enum) → write it
  //       * --provider missing → reject; the apply pre-flight would
  //         fail anyway, fail at add-repo time for a better signal
  //       * --provider invalid value → reject with allowed list
  const explicitProvider = normalizeProvider(input.provider);
  let host: string | undefined;
  try {
    host = url.startsWith('https://') ? new URL(url).hostname : undefined;
  } catch {
    host = undefined;
  }
  const canonical = host ? KNOWN_PROVIDER_HOSTS[host.toLowerCase()] : undefined;
  if (host && !canonical && !explicitProvider) {
    throw new Error(
      `Host '${host}' is not a canonical Git provider Monoceros can auto-detect (github.com / gitlab.com / bitbucket.org). Pass --provider=github|gitlab|bitbucket so the credential-helper hints know which CLI to suggest.`,
    );
  }
  if (canonical && explicitProvider && explicitProvider !== canonical) {
    throw new Error(
      `--provider=${explicitProvider} contradicts host '${host}' (auto-detected as ${canonical}). Drop --provider for canonical hosts, or fix the value.`,
    );
  }
  // For canonical hosts we don't persist `provider:` in the yml even
  // when the flag was passed (matches what auto-detection would do
  // and keeps the yml minimal). Non-canonical hosts: write the
  // explicit value as-is.
  const providerToWrite =
    !canonical && explicitProvider ? explicitProvider : undefined;
  const entry: RepoEntry = {
    url,
    path,
    ...(hasName && hasEmail
      ? {
          gitUser: {
            name: input.gitName!.trim(),
            email: input.gitEmail!.trim(),
          },
        }
      : {}),
    ...(providerToWrite ? { provider: providerToWrite } : {}),
  };
  // When a NEW repo is added and the container has no `git.user` yet,
  // scaffold a container-level identity with `${VAR}` placeholders (and
  // seed the blank keys below) — same env-managed default `init`
  // produces. Rides along in the same diff. An existing `git.user`
  // (literal or placeholder) is left untouched.
  let gitUserScaffolded = false;
  const result = await mutate(input, (doc) => {
    const repoAdded = addRepoToDoc(doc, entry);
    if (repoAdded) gitUserScaffolded = ensureContainerGitUserPlaceholder(doc);
    return repoAdded;
  });
  if (result.status === 'updated' && gitUserScaffolded) {
    const home = input.monocerosHome ?? defaultMonocerosHome();
    await ensureEnvGitignored(containerConfigsDir(home));
    await ensureEnvVars(containerEnvPath(input.name, home), input.name, [
      GIT_IDENTITY_VAR.name,
      GIT_IDENTITY_VAR.email,
    ]);
    (input.logger ?? defaultLogger()).info(
      `Added a container git.user with \${${GIT_IDENTITY_VAR.name}}/\${${GIT_IDENTITY_VAR.email}} placeholders and seeded ${input.name}.env — fill them or leave blank to use your global git identity.`,
    );
  }
  // Add the provider's CLI feature to the yml (via the existing
  // add-feature), so it's present because of the repo — github → github,
  // gitlab → gitlab, bitbucket → atlassian/twg (ADR 0035). Only when the
  // provider is known and the feature isn't already declared (idempotent,
  // and never overrides a token the builder already set).
  if (result.status === 'updated' && host) {
    const home = input.monocerosHome ?? defaultMonocerosHome();
    const prov = resolveProvider(host, entry.provider);
    const featureShort =
      prov === 'unknown' ? undefined : PROVIDER_FEATURE_SELECTOR[prov];
    if (featureShort) {
      const yml = await fs.readFile(
        containerConfigPath(input.name, home),
        'utf8',
      );
      const cfg = parseConfig(yml).config;
      const ref = (await resolveFeatureRefOrShortname(featureShort)).ref;
      const present = (cfg.features ?? []).some((f) => f.ref === ref);
      if (!present) {
        await runAddFeature({ ...input, ref: featureShort });
      }
    }
  }

  // On-the-fly clone path: if the yml change took AND the container
  // is currently running, clone the repo directly into the
  // container so the builder doesn't have to `monoceros apply`
  // afterwards. Soft-fail with a warn — failures here never roll
  // back the yml write. See ADR 0007's add-port symmetry.
  if (result.status === 'updated') {
    await tryCloneInRunningContainer(input, entry);
    await refreshBriefingFromYml(
      input.name,
      input.monocerosHome ?? defaultMonocerosHome(),
    );
  }
  return result;
}

/**
 * Best-effort: if the container is running, fetch HTTPS credentials
 * for the repo host, then `docker exec git clone …` directly into
 * `/workspaces/<name>/projects/<path>/`. Skips silently when:
 *
 *   - the container isn't running (typical case — yml-only is fine,
 *     `monoceros apply` will clone on next bring-up)
 *   - the destination folder already exists (idempotent — matches
 *     post-create.sh's "skip clone if dir exists" rule)
 *
 * Soft-fails with a warn on any error in the clone path. The yml
 * mutation is already persisted; the next `monoceros apply` will
 * retry. Tests inject the docker / credentials spawns; production
 * uses the real ones.
 */
async function tryCloneInRunningContainer(
  input: AddRepoInput,
  entry: RepoEntry,
): Promise<void> {
  const home = input.monocerosHome ?? defaultMonocerosHome();
  const root = containerDir(input.name, home);
  const logger = input.logger ?? defaultLogger();

  let containerId: string | null;
  try {
    containerId = await findRunningContainerByLocalFolder(root, {
      ...(input.containerLookupDocker
        ? { docker: input.containerLookupDocker }
        : {}),
    });
  } catch (err) {
    logger.warn(
      `Could not check whether the container is running: ${err instanceof Error ? err.message : String(err)}. The yml is updated — run \`monoceros apply ${input.name}\` to clone.`,
    );
    return;
  }
  if (!containerId) {
    logger.info(
      `Container not running — yml updated only. Clone happens on \`monoceros apply ${input.name}\`.`,
    );
    return;
  }

  // Resolve the provider token (ADR 0031) and write it into the
  // bind-mounted `.monoceros/git-credentials` so the in-container clone
  // below authenticates with it — the same token path apply uses, no
  // host git tooling involved.
  let urlHost: string;
  try {
    urlHost = new URL(entry.url).hostname;
  } catch {
    logger.warn(
      `Cannot parse URL host from ${entry.url}. The yml is updated — clone manually inside the container or rerun with a fixed URL.`,
    );
    return;
  }
  const provider = resolveProvider(urlHost, entry.provider);
  if (provider === 'unknown') {
    logger.warn(
      `Could not resolve provider for host ${urlHost}. The yml is updated; clone happens at the next \`monoceros apply\` if you set the provider.`,
    );
    return;
  }
  // Resolve the repo token (ADR 0031). A missing token is NOT fatal:
  // public repos clone read-only. We proceed to the clone and let its
  // outcome drive the messaging — the consequences/how-to-fix are shown
  // afterwards (same warn-and-proceed policy as apply).
  let noToken: MissingRepoToken | undefined;
  try {
    const { hostTokens, missing } = await resolveContainerRepoTokens(
      input.name,
      home,
      await loadComponentCatalog(),
    );
    noToken = missing.find((m) => m.host === urlHost);
    if (!noToken) {
      await collectGitCredentials(root, [{ host: urlHost, provider }], {
        patByHost: hostTokens,
        logger: { info: () => {}, warn: (m) => logger.warn(m) },
      });
    }
  } catch (err) {
    logger.warn(
      `Could not resolve a token for ${urlHost}: ${err instanceof Error ? err.message : String(err)}. The yml is updated; run \`monoceros apply ${input.name}\` to clone.`,
    );
    return;
  }

  // The clone itself. mkdir -p ensures nested parents exist. The
  // outer `[ -d <target> ] && exit 0` short-circuit matches the
  // idempotency post-create.sh has — re-running add-repo against the
  // same URL is a yml no-op anyway, but if the folder somehow
  // exists without the yml entry we still don't overwrite.
  //
  // `git -c credential.helper=…` sets the helper INLINE just for
  // this clone, instead of relying on `git config --global` having
  // been set by post-create.sh. That matters because post-create
  // runs once at container up, not on every add-repo — a container
  // that started life without any HTTPS repo wouldn't have a
  // credential.helper configured at all. Inline-setting keeps
  // add-repo self-contained and doesn't mutate the container's
  // global git config as a side effect.
  const containerName = input.name;
  const targetRel = `projects/${entry.path}`;
  const parentRel = entry.path.includes('/')
    ? `projects/${entry.path.split('/').slice(0, -1).join('/')}`
    : 'projects';
  const credentialsFile = `/workspaces/${containerName}/.monoceros/git-credentials`;
  const credentialHelper = `store --file=${credentialsFile}`;
  const script = [
    `set -eu`,
    `cd /workspaces/${containerName}`,
    `if [ -d ${shquote(targetRel)} ]; then`,
    `  echo "[add-repo] ${targetRel} already exists — skipping clone."`,
    `  exit 0`,
    `fi`,
    `mkdir -p ${shquote(parentRel)}`,
    `git -c ${shquote(`credential.helper=${credentialHelper}`)} clone ${shquote(cloneUrl(entry.url))} ${shquote(targetRel)}`,
  ];
  if (entry.gitUser) {
    script.push(
      `git -C ${shquote(targetRel)} config user.name ${shquote(entry.gitUser.name)}`,
      `git -C ${shquote(targetRel)} config user.email ${shquote(entry.gitUser.email)}`,
    );
  }
  const execFn = input.containerExec ?? realContainerExec;
  let exit;
  try {
    exit = await execFn(containerId, ['bash', '-c', script.join('\n')]);
  } catch (err) {
    logger.warn(
      `In-container clone for ${entry.url} failed: ${err instanceof Error ? err.message : String(err)}. The yml is updated; \`monoceros apply ${input.name}\` retries.`,
    );
    return;
  }
  if (exit.exitCode !== 0) {
    if (noToken) {
      // Almost always the "private repo, no token" case — show the same
      // prominent, consequence-spelling block apply uses, not a bare warn.
      // `add-repo` is by definition about a repo, so the block takes its
      // repo wording and consequences.
      process.stderr.write(
        `\n${formatUnauthenticatedRepos([noToken], input.name, { repos: true })}\n`,
      );
    } else {
      logger.warn(
        `In-container clone for ${entry.url} exited ${exit.exitCode}. The yml is updated; \`monoceros apply ${input.name}\` retries.`,
      );
    }
    return;
  }
  logger.info(
    `Cloned ${entry.url} into /workspaces/${containerName}/${targetRel} inside the running container.`,
  );
  if (noToken) {
    // Public repo cloned fine, but no token means gh/glab aren't logged
    // in and push/private-pull won't work. Light heads-up, not alarming.
    logger.warn(
      `No token set for ${urlHost}: the clone worked (public), but gh/glab aren't logged in and pushing needs a token. See ${REPO_DOCS_URL}.`,
    );
  }
  void path; // path import is reserved for future relative-path work
}

/**
 * Minimal shell-quote — single-quotes the value, escaping any
 * embedded single-quote via `'\\''`. The clone script runs inside a
 * `bash -c` invocation, so input that came from the yml (URLs,
 * paths, identity names) must be safely quoted to avoid trivial
 * injection or accidental shell-meta interpretation.
 */
function shquote(value: string): string {
  return `'${value.replace(/'/g, `'\\''`)}'`;
}

function normalizeProvider(raw: string | undefined): RepoProvider | undefined {
  if (typeof raw !== 'string') return undefined;
  const trimmed = raw.trim();
  if (trimmed.length === 0) return undefined;
  const lowered = trimmed.toLowerCase() as RepoProvider;
  if (!(PROVIDER_VALUES as readonly string[]).includes(lowered)) {
    throw new Error(
      `Invalid --provider value: ${JSON.stringify(raw)}. Allowed: ${PROVIDER_VALUES.join(', ')}.`,
    );
  }
  return lowered;
}

export function runAddFromUrl(input: AddFromUrlInput): Promise<ModifyResult> {
  const url = input.url.trim();
  if (url.length === 0) {
    throw new Error(
      'Missing URL. Usage: monoceros add-from-url <containername> <url>.',
    );
  }
  return mutate(input, (doc) => addInstallUrlToDoc(doc, url));
}

export async function runAddPort(input: AddPortInput): Promise<ModifyResult> {
  if (input.ports.length === 0) {
    throw new Error(
      'No ports given. Usage: monoceros add-port <containername> -- <port> [<port> …].',
    );
  }
  const ports = normalizePorts(input.ports);
  if (input.asDefault && ports.length > 1) {
    throw new Error(
      `--default takes exactly one port. Got: ${ports.join(', ')}. Run add-port once with --default for the new default, then again (without --default) for the rest.`,
    );
  }
  const result = await mutate(
    input,
    (doc) => {
      if (input.asDefault) {
        // --default semantics: ensure the port exists AND sits at index
        // 0. setDefaultPortInDoc covers both (insert-or-move).
        return setDefaultPortInDoc(doc, ports[0]!);
      }
      return addPortsToDoc(doc, ports);
    },
    { skipApplyReminder: true },
  );
  // Hot-reload path: when the yml actually changed, push the new
  // route set to the Traefik dynamic-config directory and make sure
  // the proxy is up. The yml is the source of truth — we re-read it
  // so the dynamic config reflects the FULL port list (including
  // entries that pre-existed this `add-port` call), not just the
  // delta. Proxy failures surface as warns but never roll back the
  // yml write. See ADR 0007.
  //
  // The sync runs even when the yml did NOT change (#74). Ports are live
  // state, and re-running add-port for an already-declared port is the
  // natural way to reconcile it: the dynamic config is rewritten, a
  // stopped proxy comes up, and a container that is not on the proxy
  // network joins it. Without this, the one command a builder reaches
  // for against a 502 is the one command that does nothing. Only the
  // briefing stays tied to an actual yml change.
  await syncPortsToProxy(input);
  if (result.status === 'updated') {
    await refreshBriefingFromYml(
      input.name,
      input.monocerosHome ?? defaultMonocerosHome(),
    );
  }
  return result;
}

/**
 * Validate each entry as an integer in [1, 65535] and dedupe — same
 * port listed twice in the CLI args is treated as one. Throws on
 * any non-integer or out-of-range value with the offending input
 * verbatim so the builder can fix the typo.
 */
function normalizePorts(raw: readonly (number | string)[]): number[] {
  const result: number[] = [];
  const seen = new Set<number>();
  for (const item of raw) {
    const n = typeof item === 'number' ? item : Number(item);
    if (!Number.isInteger(n) || n < 1 || n > 65535) {
      throw new Error(
        `Invalid port: ${JSON.stringify(item)}. Expected an integer between 1 and 65535.`,
      );
    }
    if (seen.has(n)) continue;
    seen.add(n);
    result.push(n);
  }
  return result;
}

export async function runAddFeature(
  input: AddFeatureInput,
): Promise<ModifyResult> {
  const raw = input.ref.trim();
  if (raw.length === 0) {
    throw new Error(
      'Missing feature ref. Usage: monoceros add-feature <containername> <feature>.',
    );
  }
  const resolved = await resolveFeatureRefOrShortname(raw);
  // User-supplied `-- key=value` options override the catalog-driven
  // defaults that come with a short name. For a full OCI ref the
  // resolver returns no defaults, so this is just `input.options`.
  const merged: FeatureOptions = {
    ...resolved.defaultOptions,
    ...(input.options ?? {}),
  };
  // A sub-tool selector (`atlassian/twg`) merges additively into an
  // already-present entry; a plain feature / raw ref keeps the
  // overwrite-protected behavior. `raw` is the form the builder typed,
  // echoed in the conflict error so the remove-feature hint matches.
  const result = await mutate(input, (doc) =>
    addFeatureToDoc(doc, resolved.ref, merged, {
      isPreset: resolved.isPreset,
      displayName: raw,
      containerName: input.name,
    }),
  );

  // Seed the feature's credential vars into <name>.env (the same
  // ${VAR} placeholders addFeatureToDoc just wrote into the yml), so
  // the builder only fills values. Skips keys already set with an
  // active `-- key=value`. Mirrors init; remove-feature does NOT touch
  // the env file.
  if (result.status === 'updated') {
    const summary = loadFeatureManifestSummary(resolved.ref);
    const vars = featureOptionHints(
      summary,
      resolved.ref,
      Object.keys(merged),
    ).map((h) => h.envVar);
    if (vars.length > 0) {
      const home = input.monocerosHome ?? defaultMonocerosHome();
      await ensureEnvGitignored(containerConfigsDir(home));
      const seeded = await ensureEnvVars(
        containerEnvPath(input.name, home),
        input.name,
        vars,
      );
      if (seeded.added.length > 0) {
        (input.logger ?? defaultLogger()).info(
          `Seeded ${seeded.added.join(', ')} into ${input.name}.env — fill in the values.`,
        );
      }
    }
  }
  return result;
}

/**
 * `monoceros add-mcp-server <container> <connector>` — register a catalog MCP
 * server with the agents in this container (ADR 0045).
 *
 * Writes a bare `name:` entry plus its options, and seeds the `${VAR}`
 * placeholders behind its credential options into `<name>.env`. That seeding
 * is not cosmetic: apply refuses a connector whose credential resolves empty
 * rather than registering a server that fails on first use, so the builder
 * needs the key waiting for them in the env file.
 */
export async function runAddMcpServer(
  input: AddMcpServerInput,
): Promise<ModifyResult> {
  const raw = input.connector.trim();
  if (raw.length === 0) {
    throw new Error(
      'Missing connector. Usage: monoceros add-mcp-server <containername> <connector>.',
    );
  }
  const catalog = await loadDescriptorCatalog();
  const descriptor = findMcpConnector(catalog, raw);
  if (!descriptor) {
    throw new Error(unknownMcpConnectorMessage(raw, catalog));
  }
  const doc = buildMcpConnectorDoc(descriptor);
  const options: FeatureOptions = { ...doc.options, ...(input.options ?? {}) };
  const result = await mutate(input, (d) =>
    addMcpToDoc(d, doc.name, options, {
      header: mcpHeaderCommentBefore(descriptor),
      containerName: input.name,
    }),
  );

  if (result.status === 'updated' && doc.envVars.length > 0) {
    // Only seed keys the caller did not just set with an explicit value.
    const explicit = new Set(Object.keys(input.options ?? {}));
    const vars = doc.envVars.filter(
      (v) => !explicit.has(varOptionKey(descriptor, v)),
    );
    if (vars.length > 0) {
      const home = input.monocerosHome ?? defaultMonocerosHome();
      await ensureEnvGitignored(containerConfigsDir(home));
      const seeded = await ensureEnvVars(
        containerEnvPath(input.name, home),
        input.name,
        vars,
      );
      if (seeded.added.length > 0) {
        (input.logger ?? defaultLogger()).info(
          `Seeded ${seeded.added.join(', ')} into ${input.name}.env — fill in the values before the next apply.`,
        );
      }
    }
  }
  return result;
}

/** The option key an mcp connector's derived env var came from. */
function varOptionKey(descriptor: Descriptor, envVar: string): string {
  const name = descriptor.name ?? descriptor.id;
  for (const key of Object.keys(descriptor.options)) {
    if (featureOptionVarName(name, key) === envVar) return key;
  }
  return envVar;
}

function unknownMcpConnectorMessage(
  name: string,
  catalog: Map<string, CatalogComponent>,
): string {
  const known = mcpConnectorNames(catalog).join(', ') || '(none)';
  return (
    `Unknown MCP server: ${JSON.stringify(name)}. Catalog connectors: ${known}.\n` +
    `A server the catalog does not carry goes into the yml's \`mcpServers:\` block with its own ` +
    `definition (the config its provider publishes: transport, command/url, env/headers).`
  );
}

/**
 * Accept either a full OCI feature ref (`ghcr.io/.../foo:1`) or a
 * catalog short-name (`atlassian`, `atlassian/twg`, `claude`, …).
 *
 * Short names map to the matching component's `contributes.features`
 * entry; the entry's `options` (if any) become the default option
 * values the caller's `--` overrides apply on top of. Unknown short
 * names produce an error that lists the available features.
 *
 * `isPreset` marks a sub-tool selector (`atlassian/twg`) as opposed to a
 * bare feature (`atlassian`, `claude`) or a raw OCI ref. Only a preset
 * merges additively into an already-present entry; the rest keep the
 * overwrite-protected behavior (re-adding with different options errors).
 */
async function resolveFeatureRefOrShortname(input: string): Promise<{
  ref: string;
  defaultOptions: FeatureOptions;
  isPreset: boolean;
}> {
  if (REGEX.featureRef.test(input)) {
    return { ref: input, defaultOptions: {}, isPreset: false };
  }
  const catalog = await loadComponentCatalog();
  const component = catalog.get(input);
  if (!component) {
    const featureShorts = [...catalog.values()]
      .filter((c) => c.file.category === 'feature')
      .map((c) => c.name)
      .sort();
    const knownList =
      featureShorts.length > 0 ? featureShorts.join(', ') : '(none)';
    throw new Error(
      `Unknown feature: ${JSON.stringify(input)}. ` +
        `Pass either a catalog short-name (one of: ${knownList}) ` +
        `or a full OCI ref like ` +
        `'ghcr.io/getmonoceros/monoceros-features/<name>:<tag>'.`,
    );
  }
  if (component.file.category !== 'feature') {
    throw new Error(
      `'${input}' is a ${component.file.category}, not a feature. ` +
        `Use 'monoceros add-${component.file.category} <name> ${input}' instead.`,
    );
  }
  const features = component.file.contributes.features ?? [];
  if (features.length === 0) {
    throw new Error(
      `Catalog entry '${input}' contributes no feature ref — bug or stale catalog.`,
    );
  }
  if (features.length > 1) {
    // Practically: Monoceros's own catalog has one ref per feature
    // component. A multi-ref short-name would be ambiguous because
    // `add-feature` only adds one ref at a time.
    throw new Error(
      `'${input}' bundles multiple feature refs (${features
        .map((f) => f.ref)
        .join(
          ', ',
        )}). add-feature handles one at a time — pass the OCI ref directly.`,
    );
  }
  const [first] = features;
  // `base/preset` selectors carry a `/`; a bare selector does not (a raw
  // OCI ref with slashes is handled above).
  return {
    ref: first!.ref,
    defaultOptions: { ...(first!.options ?? {}) },
    isPreset: input.includes('/'),
  };
}

// ─── remove-* ─────────────────────────────────────────────────────

export function runRemoveLanguage(
  input: RemoveLanguageInput,
): Promise<ModifyResult> {
  return mutate(input, (doc) => removeLanguageFromDoc(doc, input.language));
}

export function runRemoveService(
  input: RemoveServiceInput,
): Promise<ModifyResult> {
  return mutate(input, (doc) => removeServiceFromDoc(doc, input.service));
}

export function runRemoveAptPackages(
  input: RemoveAptPackagesInput,
): Promise<ModifyResult> {
  if (input.packages.length === 0) {
    throw new Error(
      'No package names given. Usage: monoceros remove-apt-packages <containername> -- <pkg> [<pkg> …].',
    );
  }
  return mutate(input, (doc) => removeAptPackagesFromDoc(doc, input.packages));
}

export async function runRemoveFeature(
  input: RemoveFeatureInput,
): Promise<ModifyResult> {
  const raw = input.ref.trim();
  if (raw.length === 0) {
    throw new Error(
      'Missing feature ref. Usage: monoceros remove-feature <containername> <feature>.',
    );
  }
  // Same short-name → ref resolution as `add-feature`. Without this
  // the suggestion `monoceros remove-feature atlassian` we print
  // elsewhere wouldn't actually work, only the full OCI form.
  const resolved = await resolveFeatureRefOrShortname(raw);
  return mutate(input, (doc) => removeFeatureFromDoc(doc, resolved.ref));
}

/**
 * Remove an `mcpServers:` entry. Takes the name as it stands in the yml, so it works
 * for an inline server too — not just a catalog connector.
 */
export function runRemoveMcpServer(
  input: RemoveMcpServerInput,
): Promise<ModifyResult> {
  const name = input.connector.trim();
  if (name.length === 0) {
    throw new Error(
      'Missing connector. Usage: monoceros remove-mcp-server <containername> <connector>.',
    );
  }
  return mutate(input, (doc) => removeMcpFromDoc(doc, name));
}

export function runRemoveFromUrl(
  input: RemoveFromUrlInput,
): Promise<ModifyResult> {
  const url = input.url.trim();
  if (url.length === 0) {
    throw new Error(
      'Missing URL. Usage: monoceros remove-from-url <containername> <url>.',
    );
  }
  return mutate(input, (doc) => removeInstallUrlFromDoc(doc, url));
}

export async function runRemovePort(
  input: RemovePortInput,
): Promise<ModifyResult> {
  if (input.ports.length === 0) {
    throw new Error(
      'No ports given. Usage: monoceros remove-port <containername> -- <port> [<port> …].',
    );
  }
  const ports = normalizePorts(input.ports);
  const result = await mutate(input, (doc) => removePortsFromDoc(doc, ports), {
    skipApplyReminder: true,
  });
  // Hot-reload path: same state-driven sync as add-port. When the
  // last port is gone the dynamic-config file is dropped and the
  // Traefik singleton is offered up for teardown via maybeStopProxy
  // (which no-ops if any other container is still attached). See
  // ADR 0007.
  if (result.status === 'updated') {
    await syncPortsToProxy(input);
    await refreshBriefingFromYml(
      input.name,
      input.monocerosHome ?? defaultMonocerosHome(),
    );
  }
  return result;
}

export function runRemoveRepo(input: RemoveRepoInput): Promise<ModifyResult> {
  const target = input.target.trim();
  if (target.length === 0) {
    throw new Error(
      'Missing repo identifier. Usage: monoceros remove-repo <containername> <url-or-name>.',
    );
  }
  return mutate(input, (doc) => removeRepoFromDoc(doc, target));
}

// ─── core mutate skeleton ─────────────────────────────────────────

async function mutate(
  opts: ModifyOptions,
  apply: YmlMutator,
  mutateOpts?: { skipApplyReminder?: boolean },
): Promise<ModifyResult> {
  if (!REGEX.solutionName.test(opts.name)) {
    throw new Error(
      `Invalid container name: ${JSON.stringify(opts.name)}. Use letters, digits, '.', '_' or '-'.`,
    );
  }
  const home = opts.monocerosHome ?? defaultMonocerosHome();
  const ymlPath = containerConfigPath(opts.name, home);
  const logger = opts.logger ?? defaultLogger();

  let oldText: string;
  try {
    oldText = await fs.readFile(ymlPath, 'utf8');
  } catch {
    throw new Error(
      `No such config: ${ymlPath}. Run \`monoceros init <template> ${opts.name}\` first.`,
    );
  }

  const parsed = parseConfig(oldText, ymlPath);
  const changed = apply(parsed.doc);

  if (!changed) {
    logger.info('No changes — yml is already in the desired state.');
    return { status: 'no-change' };
  }

  // Centralised post-mutation comment fixup. yaml-lib's parser
  // sometimes attaches a column-0 comment block that visually belongs
  // to the NEXT top-level pair (e.g. the `# Container ports exposed…`
  // header above `routing:`) to the previous pair's deepest trailing
  // node instead. On re-emit via the AST, the comment then drifts
  // into the previous section. We run the relocator once here so
  // every add-*/remove-* mutator gets the fix for free — without it,
  // a sequence like `init` → `add-feature` rearranges the routing /
  // repos section headers into the features block above.
  relocateLeakedSectionComments(parsed.doc);

  // Re-validate via a round-trip so schema violations introduced by
  // the mutation surface here with the regular field-path error, not
  // later at apply time.
  const newText = stringifyConfig(parsed.doc);
  parseConfig(newText, ymlPath);

  // Write straight through. The yml is a comment-preserving edit the
  // builder reviews on the next `apply` (and in git if they version it);
  // a raw unified-diff prompt here was noise nobody could act on.
  await fs.writeFile(ymlPath, newText, 'utf8');
  logger.success(`Updated ${ymlPath}.`);
  if (!mutateOpts?.skipApplyReminder) {
    logger.info(
      `Run \`monoceros apply ${opts.name}\` to rebuild the dev-container and pick up the change.`,
    );
  }
  return { status: 'updated', changedPaths: [ymlPath] };
}

function defaultLogger(): ModifyLogger {
  return {
    info: (m) => consola.info(m),
    success: (m) => consola.success(m),
    warn: (m) => consola.warn(m),
  };
}

/**
 * State-driven sync between the yml's `ports:` and Traefik's
 * dynamic-config directory + proxy lifecycle. Called from
 * `runAddPort` / `runRemovePort` after a successful yml change.
 *
 *   - ports non-empty → write `<home>/traefik/dynamic/<name>.yml`
 *     and call `ensureProxy()` (idempotent — no-op when Traefik is
 *     already up).
 *   - ports empty → remove the file and call `maybeStopProxy()`
 *     (no-op when other containers still depend on the proxy).
 *
 * Any proxy or filesystem failure is surfaced as a warn but never
 * rolls back the yml write. The yml is the source of truth; proxy
 * state is derived and self-healing on the next apply/start.
 */
async function syncPortsToProxy(
  input: AddPortInput | RemovePortInput,
): Promise<void> {
  const home = input.monocerosHome ?? defaultMonocerosHome();
  const ymlPath = containerConfigPath(input.name, home);
  const logger = input.logger ?? defaultLogger();

  let allPorts: number[];
  // Exposed services (`httpPort`) have routes in the same file. Removing the
  // last PORT therefore must not delete the file or offer the proxy for
  // teardown while a service still answers on it - the file gets rewritten with
  // just the service routes instead.
  let exposed: HttpService[] = [];
  try {
    const parsed = await readConfig(ymlPath);
    allPorts = (parsed.config.routing?.ports ?? []).map(portNumber);
    exposed = httpServices(parsed.config.services);
  } catch (err) {
    logger.warn(
      `Could not re-read yml after edit to sync Traefik routes: ${err instanceof Error ? err.message : String(err)}. The yml is correct; \`monoceros apply ${input.name}\` will rebuild the routes.`,
    );
    return;
  }

  // Effective host port for the Traefik singleton — falls back to 80
  // when monoceros-config.yml has no `routing.hostPort`. Read once per
  // sync so we have the right value for both ensureProxy and the URLs
  // we print back.
  let hostPort = 80;
  try {
    const globalConfig = await readMonocerosConfig({ monocerosHome: home });
    hostPort = proxyHostPort(globalConfig);
  } catch {
    // Bad monoceros-config.yml is the user's problem to fix; don't
    // strand the sync over it. Default 80 is the right fallback.
  }

  // Pre-flight outside the warn-only try/catch: a held host port is
  // a hard-fail (the route would never come up otherwise), and the
  // builder needs the actionable message verbatim. The yml is
  // already updated at this point — that's fine, it's the source of
  // truth and the next apply heals once the conflict is resolved.
  const hasRoutes = allPorts.length > 0 || exposed.length > 0;
  if (hasRoutes) {
    await preflightHostPort(hostPort, {
      ...(input.proxyDocker ? { docker: input.proxyDocker } : {}),
    });
  }

  try {
    if (hasRoutes) {
      await writeDynamicConfig(input.name, allPorts, {
        monocerosHome: home,
        services: exposed,
      });
      await ensureProxy({
        monocerosHome: home,
        hostPort,
        ...(input.proxyDocker ? { docker: input.proxyDocker } : {}),
        logger: { info: (m) => logger.info(m), warn: (m) => logger.warn(m) },
      });
      // A container applied without ports sits on the default bridge:
      // `--network`/`--network-alias` are creation-time args, so the
      // route we just wrote would point at an unresolvable backend and
      // answer 502. Join it live instead of sending the builder through
      // an apply the docs say they don't need (#74). No running
      // container → nothing to do; the next apply creates it attached.
      const containerId =
        allPorts.length > 0
          ? await findRunningContainerByLocalFolder(
              containerDir(input.name, home),
              {
                ...(input.containerLookupDocker
                  ? { docker: input.containerLookupDocker }
                  : {}),
              },
            )
          : undefined;
      if (containerId) {
        const outcome = await attachToProxyNetwork(containerId, input.name, {
          ...(input.proxyDocker ? { docker: input.proxyDocker } : {}),
        });
        if (outcome === 'attached') {
          logger.info(
            `Joined the running container to the monoceros-proxy network so Traefik can reach it.`,
          );
        }
      }
      const urls = proxyUrlsFor(input.name, allPorts, hostPort);
      const lines = urls.map((u) => {
        const tag = u.isDefault ? ' (default)' : '';
        return `  ${u.url}${tag}`;
      });
      // Service routes are listed too, so a `remove-port` that leaves only
      // services still shows what stays reachable instead of looking like a
      // teardown.
      for (const svc of exposed) {
        lines.push(
          `  ${serviceProxyUrl(input.name, svc.name, hostPort)}  (${svc.name})`,
        );
      }
      logger.info(`Traefik routes refreshed:\n${lines.join('\n')}`);
    } else {
      await removeDynamicConfig(input.name, { monocerosHome: home });
      await maybeStopProxy({
        monocerosHome: home,
        ...(input.proxyDocker ? { docker: input.proxyDocker } : {}),
        logger: { info: (m) => logger.info(m), warn: (m) => logger.warn(m) },
      });
    }
  } catch (err) {
    logger.warn(
      `Could not sync Traefik routes after yml edit: ${err instanceof Error ? err.message : String(err)}. The yml is correct; \`monoceros apply ${input.name}\` will rebuild the routes.`,
    );
  }
}
